/* eslint-disable no-console */
import {FC, useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {useParams} from "react-router-dom";
import {QueryResult, useLazyQuery} from "@apollo/client";
import {useFlags} from "launchdarkly-react-client-sdk";
import {ClientEntity} from "@common-core/coat-operational-hierarchy-appsync-model";
import {BackendError} from "@common-core/runtime-js/backend";
import {Button} from "@interstate/components/Button";
import {ArrowUturnLeftIcon} from "@interstate/components/Icons";
import {ErrorHandler, Interceptor} from "../../../utils";
import {AlertableError} from "../../../alerting";
import {ActivityIndicator} from "../../../activity-indicator";
import {Endpoints} from "../../../runtime";
import {
  IdentifiedRouteParams,
  useNavigation,
  useSidebarItems
} from "../../../navigation";
import {ErrorPage, PageHeading, PageTemplate} from "../../../layout";
import {EntitySourceType} from "../../EntitySourceType";
import {
  LookupClientEntityWithHierarchy,
  LookupClientEntityWithHierarchyData,
  LookupClientEntityWithHierarchyVariables
} from "../../backend";
import {CreatePlan} from "./view-model";
import {toCreatePlan} from "./transforms";
import {PlanProvider} from "./context";
import {OrganizationBuilder} from "./OrganizationBuilder";
import "./CreateOrganizationPage.scss";
import {features} from "../../../features";

export const CreateOrganizationPage: FC = () => {
  const {t} = useTranslation();
  const {id} = useParams<IdentifiedRouteParams>();
  const [error, setError] = useState<any>();
  const [source, setSource] = useState<ClientEntity | undefined>();
  const [plan, setPlan] = useState<CreatePlan | undefined>();
  const {navigateToEntityFromEvent} = useNavigation();
  const sidebarItems = useSidebarItems();
  const flags = useFlags();

  const [lookupClientEntityWithHierarchy] = useLazyQuery<
    LookupClientEntityWithHierarchyData,
    LookupClientEntityWithHierarchyVariables
  >(LookupClientEntityWithHierarchy, {
    fetchPolicy: "no-cache",
    context: {
      endpoint: Endpoints.APPSYNC
    }
  });

  const assertFound = (
    result: QueryResult<
      LookupClientEntityWithHierarchyData,
      LookupClientEntityWithHierarchyVariables
    >
  ): ClientEntity => {
    if (result.error) {
      throw new BackendError(result.error.message, 500);
    }
    if (!result.data?.entity) {
      throw new BackendError(
        t("create-organization.errors.not-found", {
          id: result.variables?.id
        }),
        404
      );
    }
    return result.data.entity;
  };

  const captureSource: Interceptor<ClientEntity> = (
    source: ClientEntity
  ): ClientEntity => {
    console.dir(source);
    setSource(source);
    return source;
  };

  const assertHierarchyPresent: Interceptor<ClientEntity> = (
    source: ClientEntity
  ): ClientEntity => {
    if (!source.hierarchy) {
      throw new AlertableError(
        t("create-organization.errors.no-hierarchy", {
          id: source.caId
        }),
        "error"
      );
    }
    return source;
  };

  const onError: ErrorHandler = (error: any) => {
    console.error(error);
    setError(error);
  };

  useEffect(() => {
    setError(undefined);
    lookupClientEntityWithHierarchy({variables: {id}})
      .then(assertFound)
      .then(captureSource)
      .then(assertHierarchyPresent)
      .then(source =>
        toCreatePlan(source, flags[features.dev.createOrg.createWithTenant])
      )
      .then(setPlan)
      .catch(onError);
  }, [id, flags]);

  // Return the ErrorPage when loading is complete and the entity was not found
  if (error) {
    return (
      <ErrorPage
        id={"create-organization-page"}
        severity={error?.severity || "error"}
        error={error.message}
        logo={false}
        banner={
          <PageHeading
            title={t("create-organization.page-title")}
            summary={t("create-organization.page-summary")}
          />
        }
        sidebarItems={sidebarItems}>
        <div className={"error-actions"}>
          {error?.status !== 404 && (
            <Button
              id={"back-to-client-button"}
              data-testid={"back-to-client-button"}
              className={"back-to-client-button"}
              buttonStyle={"tertiary"}
              startIcon={<ArrowUturnLeftIcon />}
              onClick={event => {
                navigateToEntityFromEvent(
                  event,
                  id,
                  EntitySourceType.CLIENT_HIERARCHY
                );
              }}>
              {t("create-organization.back-to-client-entity")}
            </Button>
          )}
        </div>
      </ErrorPage>
    );
  }

  // Return an empty page with the ActivityIndicator while loading the entity
  if (!source || !plan) {
    return (
      <PageTemplate
        id={"create-organization-page"}
        banner={
          <PageHeading
            title={t("create-organization.page-title")}
            summary={t("create-organization.page-summary")}
          />
        }
        sidebarItems={sidebarItems}>
        <ActivityIndicator />
      </PageTemplate>
    );
  }

  return (
    <PageTemplate
      id={"create-organization-page"}
      banner={
        <PageHeading
          title={t("create-organization.page-title")}
          summary={t("create-organization.page-summary")}
        />
      }
      sidebarItems={sidebarItems}>
      <hgroup className={"entity-header"} data-testid={"entity-header"}>
        <h2 className={"entity-business-name"}>{source.custName}</h2>
      </hgroup>
      <PlanProvider initial={plan}>
        <OrganizationBuilder />
      </PlanProvider>
    </PageTemplate>
  );
};
